import m, {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const bentoMeta = {
    tier: 'layout',
    summary: 'Bento layout — arranges its `Card` children into a feature-cell mosaic (a large hero tile plus smaller tiles).',
    when_to_use:
        'A richer alternative to `Grid` for a set of `Card`s. Pure deterministic CSS grid (no JS, so SSR and client render identically): `variant="feature"` (default) makes the first card a 2×2 hero (best with a landscape hero image); `variant="mosaic"` weaves alternating tall tiles with no single hero (suits portrait imagery like bottle shots). Collapses to a uniform grid on narrow viewports. Feed it a stably-ordered list (do not shuffle) so the layout stays consistent across renders.',
    example: `
    <Bento variant="feature">
        <Card image="/common/img/catalog/example-placeholder.jpg" title="Whisky" />
        <Card title="Rum" />
        <Card title="Gin" />
    </Bento>
    `,
} satisfies CmsComponentMeta

export interface BentoAttrs {
    /** Layout rhythm: `feature` (default) makes the first card a 2×2 hero; `mosaic` weaves alternating tall tiles (no hero). */
    variant?: 'feature' | 'mosaic'
}

/**
 * Bento layout — arranges its `Card` children into a feature-cell mosaic. Pure layout: it owns the
 * arrangement only (like `Grid`), never a heading. The mosaic is a deterministic CSS grid keyed off each
 * card's source-order position, so the server HTML and the hydrated client render are identical and a
 * reload is stable — no JS measures the DOM. Collapses to a uniform grid on narrow viewports.
 */
export class Bento extends MithrilComponent<BentoAttrs> {
    view(vnode: m.Vnode<BentoAttrs>) {
        const variant = vnode.attrs.variant === 'mosaic' ? 'mosaic' : 'feature'
        return <div className={classes('c-bento', `c-bento--${variant}`)}>{vnode.children}</div>
    }
}
